<script lang="ts">
	import { currentMarket } from '$lib/home/store';
	import { getTradeStatus } from '$lib/home/userInteract/userInteract';
	import Tooltip from '$lib/tooltip/Tooltip.svelte';
	import { LimitStatus, type minLimitOrderRange } from '$lib/types/userInteract';
	import { getMarketCurrentMarketPrice } from '$lib/util/marketHelper';
	import { getNumBackInUnits, sanitizeInputs } from '$lib/util/util';
	import BigNumber from 'bignumber.js';
	import {
		triggerInputForClosePosition,
		limitInputForClosePosition,
		ClosePositionLimitStatus,
		closePositionInput,
		closeLongOrShort,
		closePositionOrderType,
		currentClosePosition
	} from '../popupsStore';
	import { onDestroy } from 'svelte';

	export let expectedLimitFee: string;
	let estimatedProfit = '';
	let isLimitPriceEdited = false;
	export let avgEntryPrice: string;
	$: currentPrice = new BigNumber(
		getMarketCurrentMarketPrice($currentClosePosition.marketIdentifier)
	);
	const MIN_LIMIT_ORDER_RANGE: minLimitOrderRange = 0.3;

	function estimatedProfitOrLoss(
		triggerValue: string,
		expectedLimitFee: string,
		longOrShort: string
	) {
		if (!avgEntryPrice) return '0';
		let inputValue = $closePositionInput;
		let currentActiveMarket = $currentClosePosition;
		if (!triggerValue || !inputValue || !currentActiveMarket) {
			return '0';
		}

		let triggerPrice = new BigNumber(triggerValue).multipliedBy(1e18);
		let startSize = new BigNumber(avgEntryPrice).multipliedBy(inputValue);
		let endSize = new BigNumber(triggerPrice).multipliedBy(inputValue);
		let Pnl = endSize.minus(startSize);
		if (longOrShort == 'Short') return Pnl.minus(expectedLimitFee).div(1e18).toString();
		else return Pnl.multipliedBy(-1).minus(expectedLimitFee).div(1e18).toString();
	}

	const checkValidOrder = () => {
		estimatedProfit = estimatedProfitOrLoss(
			$triggerInputForClosePosition,
			expectedLimitFee,
			$closeLongOrShort
		);
		let minVal = new BigNumber($triggerInputForClosePosition).lt(
			new BigNumber($limitInputForClosePosition)
		)
			? new BigNumber($triggerInputForClosePosition)
			: new BigNumber($limitInputForClosePosition);
		if (
			new BigNumber($triggerInputForClosePosition)
				.minus($limitInputForClosePosition)
				.abs()
				.multipliedBy(100)
				.lte(minVal.multipliedBy(MIN_LIMIT_ORDER_RANGE))
		) {
			ClosePositionLimitStatus.set(LimitStatus.PriceRangeTooSmall);
		} else if (
			new BigNumber(currentPrice).div(1e18).isLessThan($triggerInputForClosePosition) &&
			new BigNumber($triggerInputForClosePosition).isGreaterThan($limitInputForClosePosition)
		) {
			ClosePositionLimitStatus.set(LimitStatus.InvalidTriggerAboveLimit);
		} else if (
			new BigNumber(currentPrice).div(1e18).isGreaterThan($triggerInputForClosePosition) &&
			new BigNumber($triggerInputForClosePosition).isLessThan($limitInputForClosePosition)
		) {
			ClosePositionLimitStatus.set(LimitStatus.InvalidLimitAboveTrigger);
		} else {
			ClosePositionLimitStatus.set(LimitStatus.Ok);
		}
	};

	const onTriggerChange = () => {
		$triggerInputForClosePosition = sanitizeInputs($triggerInputForClosePosition);
		estimatedProfit = estimatedProfitOrLoss(
			$triggerInputForClosePosition,
			expectedLimitFee,
			$closeLongOrShort
		);
		if (!isLimitPriceEdited && $triggerInputForClosePosition) {
			if (new BigNumber(currentPrice).div(1e18).lt($triggerInputForClosePosition))
				$limitInputForClosePosition = new BigNumber($triggerInputForClosePosition)
					.multipliedBy(1.05)
					.toString();
			else
				$limitInputForClosePosition = new BigNumber($triggerInputForClosePosition)
					.multipliedBy(0.95)
					.toString();
		}
		if (!$triggerInputForClosePosition) $limitInputForClosePosition = '';
		checkValidOrder();
	};

	const onLimitChange = () => {
		$limitInputForClosePosition = sanitizeInputs($limitInputForClosePosition);
		estimatedProfit = estimatedProfitOrLoss(
			$triggerInputForClosePosition,
			expectedLimitFee,
			$closeLongOrShort
		);
		checkValidOrder();
	};

	const toggleCloseViaLimit = () => {
		$triggerInputForClosePosition = '';
		$limitInputForClosePosition = '';

		if (!$triggerInputForClosePosition && !$limitInputForClosePosition && $currentMarket) {
			if ($closeLongOrShort == 'Long') {
				$triggerInputForClosePosition = new BigNumber(
					getMarketCurrentMarketPrice($currentClosePosition.marketIdentifier)
				)
					.multipliedBy(1.05)
					.div(1e18)
					.toString();

				onTriggerChange();
			}
			if ($closeLongOrShort == 'Short') {
				$triggerInputForClosePosition = new BigNumber(
					getMarketCurrentMarketPrice($currentClosePosition.marketIdentifier)
				)
					.multipliedBy(0.95)
					.div(1e18)
					.toString();

				onTriggerChange();
			}
		}
	};

	const closePositionInputEventListener = closePositionInput.subscribe(() => {
		estimatedProfit = estimatedProfitOrLoss(
			$triggerInputForClosePosition,
			expectedLimitFee,
			$closeLongOrShort
		);
		checkValidOrder();
	});

	const closePositionOrderTypeEventListener = closePositionOrderType.subscribe((val) => {
		if (val == 'Limit') {
			toggleCloseViaLimit();
		}
	});

	onDestroy(() => {
		closePositionInputEventListener();
		closePositionOrderTypeEventListener();
	});
</script>

<div class="grid gap-2 w-full my-2">
	<div
		class="flex justify-between items-center w-full border border-fx-grey-800 text-xs bg-fx-grey-800 rounded squircle py-2 px-2"
		class:border-fx-red={$ClosePositionLimitStatus != LimitStatus.Ok}
	>
		<input
			class="bg-transparent text-fx-grey-200 font-medium outline-none text-xs w-full"
			type="string"
			inputmode="decimal"
			autocomplete="off"
			autocorrect="off"
			pattern="^[0-9]*[.,]?[0-9]*$"
			minlength="1"
			maxlength="79"
			spellcheck="false"
			bind:value={$triggerInputForClosePosition}
			on:input={onTriggerChange}
		/>
		<div class=" border-l border-fx-grey-400 text-fx-grey-400 pl-2 whitespace-nowrap">
			<Tooltip header="Trigger Price" title="Price at which the order is placed by keepers.">
				Trigger Price
			</Tooltip>
		</div>
	</div>
	<div
		class="flex justify-between items-center w-full border border-fx-grey-800 text-xs bg-fx-grey-800 rounded squircle py-2 px-2"
	>
		<input
			class="bg-transparent text-fx-grey-200 font-medium outline-none text-xs w-full"
			inputmode="decimal"
			autocomplete="off"
			autocorrect="off"
			pattern="^[0-9]*[.,]?[0-9]*$"
			minlength="1"
			maxlength="79"
			spellcheck="false"
			type="string"
			bind:value={$limitInputForClosePosition}
			on:input={onLimitChange}
		/>
		<div class=" border-l border-fx-grey-400 text-fx-grey-400 pl-2 whitespace-nowrap">
			<Tooltip header="Limit Price" title="Max price for order execution.">Limit Price</Tooltip>
		</div>
	</div>
</div>

<div class="flex justify-between">
	<div class="text-fx-grey-400 text-xs font-medium">
		<Tooltip
			header="Estimated Profit"
			title="Estimated Profit when the close limit occurs. Includes approx fees"
			>Est. Profit</Tooltip
		>
	</div>

	{#if new BigNumber(estimatedProfit).gte(0)}
		<div class="text-fx-gradient-23 text-xs font-medium">
			${getNumBackInUnits(estimatedProfit, 18, 2)}
		</div>
	{:else}
		<div class="text-fx-yellow text-xs font-medium">
			{getNumBackInUnits(estimatedProfit, 18, 2)}
		</div>
	{/if}
</div>

<div class="flex justify-between">
	<div class="text-fx-grey-400 text-xs font-medium">
		<Tooltip
			header="Estimated Fees"
			title="Estimated Fees can change at the time of execution and is based on skew at that time."
			>Est. Fees (upon execution)</Tooltip
		>
	</div>

	<div class="text-fx-gradient-23 text-xs font-medium">
		{getNumBackInUnits(expectedLimitFee, 18, 1)}
	</div>
</div>

<!-- <div>
	<div class="text-fx-yellow text-xs font-medium pt-2">
		<Tooltip
			header="Requires Manual Closing"
			title="Closing via Limit is in beta and require manual cancel of orders if position is closed in other
				ways."
			>Beta: Require manual cancel if the position closed.
		</Tooltip>
	</div>
</div> -->
